import React, { useState, useEffect } from 'react';
import { useMusicPlayer } from '../contexts/MusicPlayerContext';
import {
  Music,
  Search,
  Filter,
  Download,
  Play,
  Pause,
  ChevronDown,
  X,
} from 'lucide-react';
import api from '../services/api';
import '../styles/track-list.css';
import PresignedImage from '../components/PresignedImage';
import { resolveCatalogTrackCoverImage, resolveCatalogArtistImage } from '../utils/trackImage';

const MasterTrackList = () => {
  // const { user } = useAuth();
  const { playTrack, currentTrack, isPlaying, playPause } = useMusicPlayer();
  // const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGenre, setFilterGenre] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchMasterTracks();
  }, []);

  const fetchMasterTracks = async () => {
    try {
      const response = await api.get('/api/users/master-tracks');
      setTracks(response.data.tracks);
    } catch (error) {
      console.error('Failed to fetch master tracks:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter tracks based on search and filters
  const filteredTracks = tracks.filter(track => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      track.title?.toLowerCase().includes(searchLower) ||
      track.artist_name?.toLowerCase().includes(searchLower) ||
      track.featured_artist_name?.toLowerCase().includes(searchLower) ||
      track.isrc?.toLowerCase().includes(searchLower) ||
      track.release_package?.toLowerCase().includes(searchLower) ||
      track.writer_1?.toLowerCase().includes(searchLower) ||
      track.writer_2?.toLowerCase().includes(searchLower) ||
      track.writer_3?.toLowerCase().includes(searchLower) ||
      track.writer_4?.toLowerCase().includes(searchLower) ||
      track.writer_5?.toLowerCase().includes(searchLower) ||
      track.producer?.toLowerCase().includes(searchLower) ||
      track.engineer?.toLowerCase().includes(searchLower) ||
      track.instruments?.toLowerCase().includes(searchLower) ||
      track.song_description?.toLowerCase().includes(searchLower) ||
      track.tags?.toLowerCase().includes(searchLower);
    
    const matchesGenre = !filterGenre || track.genre === filterGenre;
    
    return matchesSearch && matchesGenre;
  }).sort((a, b) =>
    (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' })
  );

  // Get unique genres for filter dropdown
  const uniqueGenres = [...new Set(tracks.map(track => track.genre).filter(Boolean))];

  const toggleExpanded = (trackId) => {
    setExpandedId((current) => (current === trackId ? null : trackId));
  };

  const renderTrackDetails = (track) => {
    const writers = [track.writer_1, track.writer_2, track.writer_3, track.writer_4, track.writer_5]
      .filter(Boolean);

    return (
      <div className="track-list-details">
        <div>
          <span className="track-list-detail-label">ISRC</span>
          <span className="track-list-detail-value">{track.isrc || '—'}</span>
        </div>
        <div>
          <span className="track-list-detail-label">Release</span>
          <span className="track-list-detail-value">{track.release_package || '—'}</span>
        </div>
        <div>
          <span className="track-list-detail-label">Key</span>
          <span className="track-list-detail-value">{track.song_key || '—'}</span>
        </div>
        <div>
          <span className="track-list-detail-label">Producer</span>
          <span className="track-list-detail-value">{track.producer || '—'}</span>
        </div>
        <div>
          <span className="track-list-detail-label">Engineer</span>
          <span className="track-list-detail-value">{track.engineer || '—'}</span>
        </div>
        <div>
          <span className="track-list-detail-label">Uploaded</span>
          <span className="track-list-detail-value">
            {track.created_at ? new Date(track.created_at).toLocaleDateString() : '—'}
          </span>
        </div>
        {writers.length > 0 && (
          <div className="track-list-writers">
            <span className="track-list-detail-label">Writers</span>
            <span className="track-list-detail-value">
              {writers.map((writer, index) => {
                const n = index + 1;
                const split = track[`writer_${n}_split`];
                const pro = track[`writer_${n}_pro`];
                return (
                  <span key={writer}>
                    {index > 0 ? ' · ' : ''}
                    {writer}
                    {split ? ` (${split}%)` : ''}
                    {pro ? ` [${pro}]` : ''}
                  </span>
                );
              })}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Handle CSV export
  const handleExportCSV = () => {
    if (filteredTracks.length === 0) {
      alert('No tracks to export');
      return;
    }

    // Define CSV headers based on available track data
    const headers = [
      'ID',
      'Title',
      'Artist Name',
      'Featured Artist',
      'Genre',
      'Sub Genre',
      'ISRC',
      'Release Package',
      'Duration',
      'BPM',
      'Key',
      'Writer 1',
      'Writer 1 Split %',
      'Writer 1 PRO',
      'Writer 2',
      'Writer 2 Split %',
      'Writer 2 PRO',
      'Writer 3',
      'Writer 3 Split %',
      'Writer 3 PRO',
      'Writer 4',
      'Writer 4 Split %',
      'Writer 4 PRO',
      'Writer 5',
      'Writer 5 Split %',
      'Writer 5 PRO',
      'Producer',
      'Engineer',
      'Instruments',
      'Song Description',
      'Tags',
      'Composition Type',
      'Lyrics Type',
      'Created At',
      'Updated At'
    ];

    // Convert tracks to CSV rows
    const csvRows = [headers];
    
    filteredTracks.forEach(track => {
      const row = [
        track.id || '',
        track.title || '',
        track.artist_name || '',
        track.featured_artist_name || '',
        track.genre || '',
        track.sub_genre || '',
        track.isrc || '',
        track.release_package || '',
        track.duration || '',
        track.bpm || '',
        track.song_key || '',
        track.writer_1 || '',
        track.writer_1_split || '',
        track.writer_1_pro || '',
        track.writer_2 || '',
        track.writer_2_split || '',
        track.writer_2_pro || '',
        track.writer_3 || '',
        track.writer_3_split || '',
        track.writer_3_pro || '',
        track.writer_4 || '',
        track.writer_4_split || '',
        track.writer_4_pro || '',
        track.writer_5 || '',
        track.writer_5_split || '',
        track.writer_5_pro || '',
        track.producer || '',
        track.engineer || '',
        track.instruments || '',
        track.song_description || '',
        track.tags || '',
        track.composition_type || '',
        track.lyrics_type || '',
        track.created_at ? new Date(track.created_at).toISOString() : '',
        track.updated_at ? new Date(track.updated_at).toISOString() : ''
      ];
      
      csvRows.push(row);
    });

    // Convert to CSV string
    const csvContent = csvRows.map(row => 
      row.map(field => {
        // Escape quotes and wrap in quotes if field contains comma, quote, or newline
        const escapedField = String(field).replace(/"/g, '""');
        if (escapedField.includes(',') || escapedField.includes('"') || escapedField.includes('\n')) {
          return `"${escapedField}"`;
        }
        return escapedField;
      }).join(',')
    ).join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `master-tracks-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-50"></div>
      </div>
    );
  }

  return (
    <div className="track-list-page track-list-page--transparent">
      <div className="track-list-header relative border-b border-white/10 overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/[0.095] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform -translate-x-full group-hover:translate-x-full" />
        <div className="relative z-10 space-y-5 p-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-white">Track Library</h1>
            <button
              onClick={handleExportCSV}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gold-50 hover:bg-gold-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-50 transition-colors hover:text-black"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
              <input
                type="text"
                placeholder="Search tracks, artist names, featured artists, ISRC, writers, producers, instruments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 w-full border border-white/20 rounded-md focus:ring-2 focus:ring-gold-50 focus:border-gold-50 bg-black/50 text-white backdrop-blur-sm placeholder-gray-500 text-sm py-2 ${searchTerm ? 'pr-10' : 'pr-4'}`}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors p-0.5"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="relative">
              <select
                value={filterGenre}
                onChange={(e) => setFilterGenre(e.target.value)}
                className="w-full appearance-none pl-4 pr-10 py-2 border border-white/20 rounded-md focus:ring-2 focus:ring-gold-50 focus:border-gold-50 bg-black/50 text-white backdrop-blur-sm text-sm"
              >
                <option value="">All Genres</option>
                {uniqueGenres.map(genre => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            </div>

            <div className="text-gray-400 flex items-center text-sm">
              <Filter className="h-4 w-4 mr-2 flex-shrink-0" />
              {filteredTracks.length} of {tracks.length} tracks
            </div>
          </div>
        </div>
      </div>

      <div className="track-list-scroll">
        {filteredTracks.length === 0 ? (
          <div className="track-list-panel p-12 text-center">
            <Music className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-400 mb-2">
              No tracks found
            </h4>
            <p className="text-gray-600 text-sm">
              Try adjusting your search or filters
            </p>
          </div>
        ) : (
          <div className="track-list-panel">
            <div className="track-list-header-row">
              <span />
              <span aria-hidden="true" />
              <span>Artist</span>
              <span>Track</span>
              <span>Genre</span>
              <span>Time</span>
              <span>BPM</span>
              <span />
            </div>
            {filteredTracks.map((track) => {
              const isActive = currentTrack?.id === track.id;
              const isPlayingThis = isActive && isPlaying;
              const isExpanded = expandedId === track.id;
              const trackCover = resolveCatalogTrackCoverImage(track);
              const artistCover = resolveCatalogArtistImage(track);
              const primaryImage = trackCover.assetKey ? trackCover : artistCover;
              const secondaryImage =
                trackCover.assetKey && artistCover.assetKey ? artistCover : null;
              const artistLabel = [
                track.artist_name || 'Unknown Artist',
                track.featured_artist_name ? `feat. ${track.featured_artist_name}` : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <div key={track.id} className="track-list-item">
                  <div
                    className={`track-list-row ${isActive ? 'track-list-row--active' : ''}`}
                    onClick={() => toggleExpanded(track.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleExpanded(track.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {track.url ? (
                      <button
                        type="button"
                        className="track-list-play"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isPlayingThis) {
                            playPause();
                          } else {
                            playTrack(track, filteredTracks);
                          }
                        }}
                        title={isPlayingThis ? 'Pause' : 'Play'}
                      >
                        {isPlayingThis ? (
                          <Pause className="h-3.5 w-3.5" />
                        ) : (
                          <Play className="h-3.5 w-3.5 ml-0.5" />
                        )}
                      </button>
                    ) : (
                      <div className="track-list-play track-list-play--disabled">
                        <Music className="h-3.5 w-3.5" />
                      </div>
                    )}

                    <div className="track-list-image-col">
                      <div className="track-list-image-slot">
                        {primaryImage.assetKey ? (
                          <div className="track-list-image">
                            <PresignedImage
                              assetKey={primaryImage.assetKey}
                              assetType={primaryImage.assetType}
                              alt={track.title || track.artist_name || 'Track'}
                              resolved
                            />
                          </div>
                        ) : (
                          <div className="track-list-image track-list-image--placeholder" aria-hidden="true" />
                        )}
                      </div>
                      <div className="track-list-image-slot">
                        {secondaryImage ? (
                          <div className="track-list-image">
                            <PresignedImage
                              assetKey={secondaryImage.assetKey}
                              assetType={secondaryImage.assetType}
                              alt={track.artist_name || 'Artist'}
                              resolved
                            />
                          </div>
                        ) : (
                          <div className="track-list-image track-list-image--empty" aria-hidden="true" />
                        )}
                      </div>
                    </div>

                    <div className="track-list-artist-col">
                      <div className="track-list-artist">{artistLabel}</div>
                    </div>

                    <div className="track-list-title-col">
                      <div className="track-list-title">{track.title || 'Untitled'}</div>
                    </div>

                    <div className="track-list-tags">
                      {track.genre ? (
                        <span className="track-list-tag">{track.genre.toUpperCase()}</span>
                      ) : (
                        <span className="track-list-tag">—</span>
                      )}
                      {track.sub_genre && (
                        <span className="track-list-tag">{track.sub_genre.toUpperCase()}</span>
                      )}
                    </div>

                    <div className="track-list-meta">{track.duration || '—'}</div>
                    <div className="track-list-meta">{track.bpm ? `${track.bpm}` : '—'}</div>

                    <button
                      type="button"
                      className={`track-list-expand ${isExpanded ? 'track-list-expand--open' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(track.id);
                      }}
                      aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                  {isExpanded && renderTrackDetails(track)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MasterTrackList;
